// Shared date + timetable utilities.

export function isoDate(d) {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
}

export function parseIsoDate(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function weekStartOf(d) {
  // ISO week: Monday = 0
  const day = (d.getDay() + 6) % 7; // 0..6
  const monday = new Date(d);
  monday.setDate(d.getDate() - day);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

export function addDays(d, days) {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + days);
  return nd;
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export function dayLabel(i) { return DAY_LABELS[i]; }
export function dayLong(i) { return DAY_LONG[i]; }

export function formatWeekRange(monday) {
  const sun = addDays(monday, 6);
  const opts = { day: "numeric", month: "short" };
  return `${monday.toLocaleDateString("en-GB", opts)} – ${sun.toLocaleDateString("en-GB", opts)}`;
}

export function todayIsoDate() { return isoDate(new Date()); }
export function todayDayOfWeek() { return (new Date().getDay() + 6) % 7; }

// Slot types
export const SLOT_STUDY = "study";
export const SLOT_BREAK = "break";
export const SLOT_DINNER = "dinner";
export const SLOT_WIND = "wind_down";

// Slot templates — one per weekday and weekend.
// Each slot: { type, start: "HH:MM", end: "HH:MM", duration }
function buildWeekdaySlots() {
  return [
    { type: SLOT_STUDY,  start: "16:00", end: "16:45", duration: 45 },
    { type: SLOT_BREAK,  start: "16:45", end: "17:00", duration: 15 },
    { type: SLOT_STUDY,  start: "17:00", end: "17:45", duration: 45 },
    { type: SLOT_BREAK,  start: "17:45", end: "18:00", duration: 15 },
    { type: SLOT_WIND,   start: "18:00", end: "18:30", duration: 30 },
    { type: SLOT_DINNER, start: "18:30", end: "19:30", duration: 60 },
    { type: SLOT_STUDY,  start: "19:30", end: "20:15", duration: 45 },
    { type: SLOT_BREAK,  start: "20:15", end: "20:30", duration: 15 },
    { type: SLOT_STUDY,  start: "20:30", end: "21:15", duration: 45 },
    { type: SLOT_BREAK,  start: "21:15", end: "21:30", duration: 15 },
    { type: SLOT_WIND,   start: "21:30", end: "22:00", duration: 30 },
  ];
}
function buildWeekendSlots() {
  const arr = [];
  // 10:00-17:45 alternating 45/15
  for (let h = 10; h <= 17; h++) {
    const hh = String(h).padStart(2, "0");
    const nn = String(h + 1).padStart(2, "0");
    arr.push({ type: SLOT_STUDY, start: `${hh}:00`, end: `${hh}:45`, duration: 45 });
    arr.push({ type: SLOT_BREAK, start: `${hh}:45`, end: `${nn}:00`, duration: 15 });
  }
  // 18:00-20:00: wind_down + dinner + wind_down
  arr.push({ type: SLOT_WIND,   start: "18:00", end: "18:30", duration: 30 });
  arr.push({ type: SLOT_DINNER, start: "18:30", end: "19:30", duration: 60 });
  arr.push({ type: SLOT_WIND,   start: "19:30", end: "20:00", duration: 30 });
  return arr;
}
const WEEKDAY_SLOTS = buildWeekdaySlots();
const WEEKEND_SLOTS = buildWeekendSlots();

export function slotsForDay(dayOfWeek) {
  return dayOfWeek >= 5 ? WEEKEND_SLOTS : WEEKDAY_SLOTS;
}

export function studyStartsForDay(dayOfWeek) {
  return slotsForDay(dayOfWeek).filter((s) => s.type === SLOT_STUDY).map((s) => s.start);
}

export function isStudySlot(dayOfWeek, start) {
  return studyStartsForDay(dayOfWeek).includes(start);
}

// Given a block's day/time compute whether it has been "missed" (past its window and not completed).
export function isMissed(block, weekStartDateStr) {
  if (block.status === "completed" || block.status === "in_progress") return false;
  const ws = parseIsoDate(weekStartDateStr);
  const day = addDays(ws, block.day_of_week);
  const [h, m] = block.start_time.split(":").map(Number);
  const endMin = h * 60 + m + (block.duration_minutes || 45);
  const end = new Date(day);
  end.setHours(0, 0, 0, 0);
  end.setMinutes(endMin);
  return end.getTime() < Date.now();
}

export function fmtMinutesAsHours(mins) {
  const h = mins / 60;
  if (Number.isInteger(h)) return `${h}h`;
  return `${h.toFixed(2).replace(/\.?0+$/, "")}h`;
}
